import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Flame, Wind, User as UserIcon, Home as HomeIcon } from "lucide-react";
import { api } from "@/lib/api";
import NotificationsBell from "@/components/NotificationsBell";

export default function Header({ user }) {
  const [streak, setStreak] = useState(0);
  const { pathname } = useLocation();

  useEffect(() => {
    let mounted = true;
    api.get(`/users/${user.id}/streak`).then(({ data }) => {
      if (mounted) setStreak(data.streak);
    });
    return () => {
      mounted = false;
    };
  }, [user.id, pathname]);

  const nav = [
    { to: "/", label: "Feed", icon: HomeIcon, testId: "nav-feed" },
    { to: "/mood", label: "Mood", icon: Wind, testId: "nav-mood" },
    { to: "/profile", label: "Profile", icon: UserIcon, testId: "nav-profile" },
  ];

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-xl border-b"
      style={{ background: "rgba(12, 11, 14, 0.82)", borderColor: "var(--exhale-border)" }}
      data-testid="app-header"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
          <span className="font-display text-2xl font-extrabold ember-text tracking-tight">twatter</span>
          <span className="hidden sm:inline text-xs font-medium tracking-widest uppercase" style={{ color: "var(--exhale-text-muted)" }}>
            vent. don&apos;t bottle it.
          </span>
        </Link>

        <div className="flex items-center gap-2 sm:gap-3">
          <div
            className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full border"
            style={{ borderColor: "var(--exhale-border)", background: "var(--exhale-surface)" }}
            data-testid="streak-badge"
            title={`${streak}-day streak. Keep exhaling.`}
          >
            <Flame className={`w-4 h-4 ${streak > 0 ? "flame-pulse" : ""}`} style={{ color: streak > 0 ? "#FF5A36" : "#6E6778" }} />
            <span className="text-sm font-semibold font-mono" style={{ color: streak > 0 ? "#FF5A36" : "#A19BAA" }}>
              {streak}
            </span>
          </div>

          <NotificationsBell user={user} />

          <nav className="flex items-center gap-1 rounded-full p-1 border" style={{ borderColor: "var(--exhale-border)", background: "var(--exhale-surface)" }}>
            {nav.map(({ to, label, icon: Icon, testId }) => {
              const active = pathname === to;
              return (
                <Link
                  key={to}
                  to={to}
                  data-testid={testId}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors"
                  style={{
                    background: active ? "var(--exhale-accent)" : "transparent",
                    color: active ? "#0C0B0E" : "var(--exhale-text-2)",
                  }}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{label}</span>
                </Link>
              );
            })}
          </nav>
        </div>
      </div>
    </header>
  );
}
